import React from 'react';
import './CarCalculatorInfoPage.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="info-page">
      <div className="info-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: October 3, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Larry's Help ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and financial planning tools.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password.</li>
            <li><strong>Google Account Information:</strong> If you sign up using Google, we collect your Google profile information including name, email, and profile picture.</li>
            <li><strong>Financial Scenarios:</strong> We store the financial scenarios and calculations you save, including car comparisons, housing analyses, and retirement planning data.</li>
            <li><strong>Payment Information:</strong> When you subscribe to supporter features, payment information is processed securely through Stripe. We do not store your credit card details.</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Usage Data:</strong> We collect information about how you interact with our website, including pages visited, features used, and time spent on the site.</li>
            <li><strong>Device Information:</strong> We may collect information about your device, browser type, operating system, and IP address.</li>
            <li><strong>Cookies and Similar Technologies:</strong> We use cookies and similar tracking technologies to enhance your experience and analyze site usage.</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our financial planning tools and services</li>
            <li>Process your transactions and manage your subscriptions</li>
            <li>Send you account-related notifications and updates</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Analyze usage patterns to improve our website and user experience</li>
            <li>Detect, prevent, and address technical issues or fraudulent activity</li>
            <li>Comply with legal obligations and enforce our terms of service</li>
          </ul>
        </section>

        <section>
          <h2>4. Third-Party Services</h2>

          <h3>4.1 Google AdSense</h3>
          <p>
            We use Google AdSense to display advertisements on our website. Google AdSense uses cookies to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google's Ads Settings</a>.
          </p>
          <p>
            For more information about Google's privacy practices, please visit the <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google Privacy & Terms page</a>.
          </p>

          <h3>4.2 Google Analytics</h3>
          <p>
            We may use Google Analytics to analyze website traffic and usage patterns. Google Analytics uses cookies to collect information anonymously and report website trends. You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
          </p>

          <h3>4.3 Stripe Payment Processing</h3>
          <p>
            We use Stripe to process payments securely. When you make a payment, your payment information is transmitted directly to Stripe and is not stored on our servers. Stripe's privacy policy is available at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>.
          </p>

          <h3>4.4 Google Sign-In</h3>
          <p>
            We offer Google Sign-In as a convenient authentication method. When you use Google Sign-In, we receive basic profile information from your Google account. Google's privacy policy governs the information Google collects. For more information, visit <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
          </p>
        </section>

        <section>
          <h2>5. Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar tracking technologies to:</p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Authenticate your account and maintain your session</li>
            <li>Analyze site traffic and user behavior</li>
            <li>Serve personalized advertisements through Google AdSense</li>
          </ul>
          <p>
            You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
          </p>
        </section>

        <section>
          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit using HTTPS/SSL</li>
            <li>Secure password hashing and storage</li>
            <li>Regular security assessments and updates</li>
            <li>Limited access to personal information on a need-to-know basis</li>
          </ul>
          <p>
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>7. Scheduling Data</h2>
          <p>When you use our scheduling platform, we collect and store:</p>
          <ul>
            <li><strong>Booking Information:</strong> Names, email addresses, phone numbers, meeting times, and notes provided by you and your invitees.</li>
            <li><strong>Calendar Access Tokens:</strong> If you connect Google Calendar or Microsoft Outlook, we store encrypted OAuth tokens to sync your availability and create calendar events. You can revoke access at any time through your calendar provider's settings.</li>
            <li><strong>Payment Data:</strong> If you accept payments through scheduling, payments are processed via Stripe Connect. We store transaction references but not payment card details.</li>
          </ul>
          <p>
            Invitees who book meetings with you are informed that their contact information is shared with you (the host) and stored by Larry's Help to facilitate the booking.
          </p>
        </section>

        <section>
          <h2>8. Accounting and Financial Data</h2>
          <p>When you use our accounting and bookkeeping tools, we collect and store:</p>
          <ul>
            <li><strong>Business Records:</strong> Business name, type, contact information, and financial data including journal entries, chart of accounts, and transaction records.</li>
            <li><strong>Bank Import Data:</strong> If you upload bank CSV files, we store the imported transactions for categorization and reconciliation.</li>
            <li><strong>Audit Logs:</strong> We log accounting actions (creating, posting, and voiding journal entries; account changes) for security, compliance, and your protection. Audit logs include the action performed, timestamp, user identity, and IP address.</li>
          </ul>
          <p>
            Your financial data is accessible only to users you have granted access to your business account. We do not access, review, or share your financial records except as required by law.
          </p>
        </section>

        <section>
          <h2>9. Data Retention</h2>
          <ul>
            <li><strong>Account Data:</strong> Retained for as long as your account is active.</li>
            <li><strong>Financial Scenarios:</strong> Retained for as long as your account is active.</li>
            <li><strong>Scheduling Bookings:</strong> Retained for 2 years after the booking date, then automatically deleted.</li>
            <li><strong>Accounting Records:</strong> Retained for 7 years to comply with CRA (Canada Revenue Agency) record-keeping requirements, even if your account is closed.</li>
            <li><strong>Audit Logs:</strong> Retained for 7 years per CRA compliance requirements.</li>
          </ul>
          <p>
            If you wish to delete your account or request that we no longer use your information, please contact us. Note that accounting records may be retained as required by law.
          </p>
        </section>

        <section>
          <h2>10. Your Rights and Choices</h2>
          <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
          <ul>
            <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
            <li><strong>Opt-Out:</strong> Opt out of marketing communications or personalized advertising</li>
          </ul>
          <p>
            To exercise these rights, please contact us at the email address provided below.
          </p>
        </section>

        <section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information.
          </p>
        </section>

        <section>
          <h2>12. International Users</h2>
          <p>
            If you are accessing our website from outside Canada, please note that your information may be transferred to, stored, and processed in Canada or other countries where our servers are located. By using our services, you consent to this transfer.
          </p>
        </section>

        <section>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, regulatory, or operational reasons. We will notify you of any material changes by posting the updated policy on this page with a new "Last Updated" date. We encourage you to review this Privacy Policy periodically.
          </p>
        </section>

        <section>
          <h2>14. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:privacy@larryshelp.org">privacy@larryshelp.org</a>
          </p>
        </section>

        <section>
          <h2>15. GDPR Compliance (For European Users)</h2>
          <p>
            If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
          </p>
          <ul>
            <li>The right to be informed about our data collection and use</li>
            <li>The right to access your personal data</li>
            <li>The right to rectification of inaccurate data</li>
            <li>The right to erasure ("right to be forgotten")</li>
            <li>The right to restrict processing</li>
            <li>The right to data portability</li>
            <li>The right to object to processing</li>
            <li>Rights related to automated decision-making and profiling</li>
          </ul>
          <p>
            Our legal basis for processing your personal information includes: your consent, performance of a contract, compliance with legal obligations, and our legitimate business interests.
          </p>
        </section>

        <section>
          <h2>16. California Privacy Rights (CCPA)</h2>
          <p>
            If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
          </p>
          <ul>
            <li>Right to know what personal information is collected, used, shared, or sold</li>
            <li>Right to delete personal information held by businesses</li>
            <li>Right to opt-out of sale of personal information (we do not sell personal information)</li>
            <li>Right to non-discrimination for exercising CCPA rights</li>
          </ul>
        </section>

        <div style={{ marginTop: '3rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #dc3545' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
            By using Larry's Help, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
